import { useEffect, useId, useRef, useState } from "react";
import { CloseIcon } from "../../assets/icons/closeIcon";
import { DropdownIcon } from "../../assets/icons/dropdownIcon";
import { styled } from "styled-components";
import { BinSmallTrashIcon } from "../../assets/icons/binsmall";
import { Text } from "../../utils/text";
import {
  StyledCursorRow,
  StyledLabel,
  StyledSelectedArea,
  StyledSelectedValue,
  StyledTags,
  StyledTagsRow,
} from "./FormFieldStyles";
import {
  Checkbox,
  RequiredIndicator,
  SelectIcon as DropdownIconWrapper,
  SelectMenu as DropdownMenu,
  SelectOption,
  SelectRoot as DropdownWrapper,
  SelectTrigger as DropdownButton,
} from "./Form";
import {
  LegendShapeIcon as StyledLegendShapeIcon,
  LegendSymbol as StyledLegendSymbol,
} from "./Selection";
import { designSystemVars as vars } from "../tokens";
import { InlineIconButton } from "./Button";

export interface MultiSelectDropdownOption {
  label: string;
  value: string;
  isDisabled?: boolean;
}

interface Props {
  label: string;
  data: MultiSelectDropdownOption[];
  value?: string[];
  onChange: (value: string[]) => void;
  width?: string;
  isRequired?: boolean;
  searchable?: boolean;
  maxSelections?: number;
  onScrollToEnd?: () => void;
  loading?: boolean;
  searchPlaceholder?: string;
}

const legendShapes = ["circle", "square", "triangle"] as const;
const colors = [
  "var(--inzeloop-ds-color-action-primary)",
  "var(--inzeloop-ds-color-text-disabled)",
  "var(--inzeloop-ds-color-workflow-muted)",
];
const dashArrays = [undefined, "7 4", "2 3"];

export const MultiSelectDropdownField: React.FC<Props> = ({
  label,
  data,
  value = [],
  onChange,
  width = "225px",
  isRequired = false,
  searchable = false,
  maxSelections,
  onScrollToEnd,
  loading = false,
  searchPlaceholder = "Search Asqio Database",
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [isKeyboardFocus, setIsKeyboardFocus] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const generatedId = useId();
  const labelId = `${generatedId}-label`;
  const valueId = `${generatedId}-value`;
  const menuId = `${generatedId}-menu`;

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
        setIsFocused(false);
      }
    };

    document.addEventListener("click", handleClick, true);
    return () => document.removeEventListener("click", handleClick, true);
  }, []);

  useEffect(() => {
    if (!open) setSearch("");
  }, [open]);

  const filteredData = searchable
    ? data.filter((d) => d.label.toLowerCase().includes(search.toLowerCase()))
    : data;

  const selectedOptions = data.filter((d) => value.includes(d.value));
  const showCursor = searchable && isFocused;
  const toggleOption = (optionValue: string) => {
    const alreadySelected = value.includes(optionValue);

    if (alreadySelected) {
      onChange(value.filter((item) => item !== optionValue));
      return;
    }

    if (maxSelections && value.length >= maxSelections) {
      return;
    }

    onChange([...value, optionValue]);
  };

  const removeSelected = (
    optionValue: string,
    e: React.MouseEvent<HTMLSpanElement>,
  ) => {
    e.stopPropagation();
    onChange(value.filter((item) => item !== optionValue));
  };

  const handleOpen = () => {
    if (!open) {
      if (searchable) setIsFocused(true);
      setOpen(true);
    } else {
      setOpen(false);
      setIsFocused(false);
    }
  };

  return (
    <DropdownWrapper ref={ref} style={{ width, height: "auto" }}>
      <DropdownButton
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        aria-labelledby={`${labelId} ${valueId}`}
        onClick={() => {
          setIsKeyboardFocus(false);
          handleOpen();
        }}
        onBlur={() => {
          setIsKeyboardFocus(false);
          setIsFocused(false);
        }}
        tabIndex={0}
        $open={open}
        $keyboardFocus={isKeyboardFocus}
        style={{
          height: "auto",
          minHeight: "65px",
        }}
        onFocus={(e) => {
          if (e.relatedTarget !== null) {
            setIsKeyboardFocus(true);
          }
          setIsFocused(true);
        }}
        onKeyDown={(e) => {
          if (e.key === "Tab") return;

          if (["Space", "ArrowUp", "ArrowDown"].includes(e.code)) {
            e.preventDefault();
          }

          if (!open) setOpen(true);
          setIsFocused(true);
          setIsKeyboardFocus(false);

          if (!searchable) return;

          if (e.key === "Backspace") {
            setSearch((prev) => prev.slice(0, -1));
            return;
          }

          if (e.key.length === 1) {
            setSearch((prev) => prev + e.key);
          }
        }}
      >
        <StyledLabel as="span" id={labelId} $active={open}>
          {label} {isRequired && <RequiredIndicator>*</RequiredIndicator>}
        </StyledLabel>

        <StyledSelectedArea id={valueId}>
          <StyledTagsRow>
            {selectedOptions.map((option, index) => (
              <StyledTags key={option.value}>
                <Text
                  as="div"
                  fontFamily={vars.typography.family.sans}
                  fontSize="var(--inzeloop-ds-font-size-sm)"
                  fontStyle="normal"
                  fontWeight="var(--inzeloop-ds-font-weight-regular)"
                  $lineHeight="var(--inzeloop-ds-line-height-sm)"
                  $margin="var(--inzeloop-ds-spacing-none)"
                >
                  <div
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "var(--inzeloop-ds-spacing-sm)",
                    }}
                  >
                    <StyledLegendSymbol
                      style={{ width: "26px" }}
                      $color={
                        colors[index] ||
                        "var(--inzeloop-ds-color-action-primary)"
                      }
                      $dashArray={dashArrays[index]}
                    >
                      <StyledLegendShapeIcon
                        style={
                          legendShapes[index] !== "triangle"
                            ? { width: "8px", height: "8px" }
                            : {}
                        }
                        $color={
                          colors[index] ||
                          "var(--inzeloop-ds-color-action-primary)"
                        }
                        $shape={legendShapes[index] || "circle"}
                      />
                    </StyledLegendSymbol>
                    {option.label}
                  </div>
                </Text>

                <InlineIconButton
                  variant="danger"
                  aria-label={`Remove ${option.label}`}
                  onClick={(e) => removeSelected(option.value, e)}
                >
                  <BinSmallTrashIcon />
                </InlineIconButton>
              </StyledTags>
            ))}
          </StyledTagsRow>

          {searchable && (
            <StyledCursorRow>
              <StyledSelectedValue
                $hasCursor={showCursor}
                $isPlaceholder={!search && !showCursor}
              >
                {searchable && search
                  ? search
                  : showCursor
                    ? "\u00A0"
                    : searchPlaceholder}
              </StyledSelectedValue>
            </StyledCursorRow>
          )}
        </StyledSelectedArea>

        <DropdownIconWrapper
          onClick={(e) => {
            e.stopPropagation();
            setIsKeyboardFocus(false);
            handleOpen();
          }}
          style={{
            position: "absolute",
            top: "18px",
            right: "8px",
            zIndex: 2,
          }}
        >
          {open ? <CloseIcon /> : <DropdownIcon />}
        </DropdownIconWrapper>
      </DropdownButton>

      {open && (
        <DropdownMenu
          id={menuId}
          role="listbox"
          aria-multiselectable="true"
          onScroll={(e) => {
            const el = e.currentTarget;
            const isNearBottom =
              el.scrollTop + el.clientHeight >= el.scrollHeight - 10;

            if (isNearBottom && onScrollToEnd && !loading) {
              onScrollToEnd();
            }
          }}
          style={{
            top: "102%",
            paddingRight: "var(--inzeloop-ds-spacing-xs)",
            paddingBottom: "var(--inzeloop-ds-spacing-md)",
            paddingTop: "var(--inzeloop-ds-spacing-5px)",
            maxHeight: "224px",
            overflowY: "auto",
          }}
        >
          {filteredData.length === 0 && (
            <div
              style={{
                padding: "var(--inzeloop-ds-spacing-sm)",
                color: vars.color.control.textEmpty,
              }}
            >
              No results found
            </div>
          )}

          {filteredData.map((option) => {
            const checked = value.includes(option.value);
            const disabled =
              (!checked && !!maxSelections && value.length >= maxSelections) ||
              option.isDisabled;

            return (
              <Option
                disabled={disabled}
                key={option.value}
                role="option"
                aria-selected={checked}
                aria-disabled={disabled}
                onClick={(e: any) => {
                  e.stopPropagation();
                  if (!disabled) {
                    toggleOption(option.value);
                  }
                }}
                style={{
                  opacity: disabled ? 0.5 : 1,
                  cursor: disabled ? "not-allowed" : "default",
                }}
              >
                <Checkbox checked={checked} readOnly />
                {option.label}{" "}
                {option.isDisabled ? "(Scorecard Version Mismatch)" : ""}
              </Option>
            );
          })}
        </DropdownMenu>
      )}
    </DropdownWrapper>
  );
};

export const Option = styled(SelectOption).attrs<{ disabled?: boolean }>(
  ({ disabled }) => ({ $disabled: disabled }),
)<{ disabled?: boolean }>`
  height: 32px;
`;
